<template>
  <div>
    <div class="header-section">
      <div class="content-section">
        <h3 class="section-title">
          {{ $t("formsGenerator") }}
        </h3>

        <LoadingSpinner v-if="loading" />

        <div
          v-else-if="formsInactive"
          class="p-4 text-center bg-yellow-50 rounded-lg mt-5"
        >
          <p class="text-yellow-700">
            {{ $t("formsRequiredError") }}
          </p>
        </div>

        <!-- Form Notification Settings -->
        <div v-else class="content mb-5">
          <div class="flex justify-between items-center mb-4">
            <button @click="openEditModal" class="BTNMAIN py-3 px-6 flex gap-2 items-center">
              <i class="icon-smsir-plus text-[20px]"></i>
              <span class="text-[#0D0D0D] text-base">{{ $t("createNewRow") }}</span>
            </button>
          </div>

          <!-- No rows message -->
          <div
            v-if="notificationRows.length === 0"
            class="no-templates p-4 text-center bg-gray-50 rounded-lg"
          >
            <p class="text-gray-500">
              {{ $t("noNotificationRowsYet") }}
            </p>
            <p class="text-gray-500 text-sm mt-2">
              {{ $t("clickCreateNewRowToAdd") }}
            </p>
          </div>

          <!-- Notification Rows Table -->
          <div v-else class="overflow-x-auto">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-gray-100">
                  <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('formType') }}</th>
                  <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('formName') }}</th>
                  <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('sendTo') }}</th>
                  <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in notificationRows" :key="index" class="border-b hover:bg-gray-50">
                  <td class="p-3 text-base text-[#0D0D0D]">
                    {{ getFormTypeText(row.form_type) }}
                  </td>
                  <td class="p-3 text-base text-[#0D0D0D]">
                    {{ getFormTitle(row.form_id, row.form_type) }}
                  </td>
                  <td class="p-3 text-base text-[#0D0D0D]">
                    {{ getRecipientText(row.recipient_type) }}
                  </td>
                  <td class="p-3">
                    <div class="flex gap-2">
                      <button @click="editRow(index)" class="text-[#0D0D0D] hover:text-blue-600">
                        <i class="icon-smsir-write text-3xl"></i>
              </button>
                      <button @click="deleteRow(index)" class="text-[#0D0D0D] hover:text-red-600">
                        <i class="icon-smsir-trash-bin-trash text-2xl"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Row Modal -->
    <div v-if="showEditModal" class="modal">
      <div class="modal-content max-w-[900px]">
        <div class="modal-header">
          <h2 class="text-[18px] text-[#0D0D0D]">{{ isNewRow ? $t('addNewForm') : $t('editForm') }}</h2>
          <button @click="closeModal" class="text-2xl">&times;</button>
        </div>
        
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="modalLoading || loadingForms" class="flex justify-center items-center h-[400px]">
            <LoadingSpinner />
          </div>

          <!-- Content when not loading -->
          <div v-else>
            <div class="flex flex-wrap gap-4 mb-4">
              <!-- Form Type -->
              <div class="flex-1 min-w-[250px]">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('formType') }}</label>
      <select 
                  v-model="currentRow.form_type" 
        class="input-main w-full"
                  :disabled="!isNewRow"
                  @change="handleFormTypeChange"
                >
                  <option value="">{{ $t('selectFormType') }}</option>
                  <option v-for="form in availableFormTypes" :key="form" :value="form">
                    {{ form === 'gravity_forms' ? $t('gravityForms') : form === 'contact_form_7' ? $t('contactForm7') : $t('wpforms') }}
        </option>
      </select>
    </div>
              
              <!-- Form ID -->
              <div class="flex-1 min-w-[250px]">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('selectForm') }}</label>
    <select 
                  v-model="currentRow.form_id" 
      class="input-main w-full"
                  :disabled="!currentRow.form_type"
                  @change="loadFormFields(currentRow)"
      >
        <option value="">{{ $t('selectForm') }}</option>
        <option v-for="form in availableForms" :key="form.id" :value="form.id">
          {{ form.title }}
        </option>
    </select>
          </div>

              <!-- Recipient Types -->
              <div class="flex-1 min-w-[250px]">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('sendTo') }}</label>
                <div class="flex gap-4 mt-2 py-3">
                  <label class="flex items-center">
                    <input 
                      type="checkbox" 
                      v-model="editRecipientTypes" 
                      value="admin" 
                      class="ml-2 border border-[#D9D9D9] rounded-[7px] size-[20px]"
                    >
                    <span>{{ $t('admin') }}</span>
                  </label>
                  <label class="flex items-center">
                    <input 
                      type="checkbox" 
                      v-model="editRecipientTypes" 
                      value="user" 
                      class="ml-2 border border-[#D9D9D9] rounded-[7px] size-[20px]"
                    >
                    <span>{{ $t('user') }}</span>
                  </label>
        </div>
      </div>
    </div>

            <!-- Admin Configuration -->
            <div v-if="editRecipientTypes.includes('admin')" class="mb-6 p-4 border border-gray-200 rounded-lg">
              <h3 class="text-lg font-medium mb-3">{{ $t('adminConfiguration') }}</h3>
    
    <div class="mb-4">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('adminMobileNumbers') }}</label>
      <input
                  v-model="currentRow.config.admin_mobile_numbers"
        type="text"
        class="input-main w-full"
        :placeholder="$t('enterCommaSeperatedNumbers')"
      />
      <small class="text-gray-500">{{ $t('commaSeperatedNumbers') }}</small>
    </div>
    
    <div class="mb-4">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
      <input
                  v-model="currentRow.config.admin_template_id"
        type="number"
        class="input-main w-full"
        :placeholder="$t('templateId')"
      />
    </div>
    
    <div class="mb-4">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('messageTemplate') }}</label>
      <div class="relative">
                  <input
                    v-model="currentRow.config.admin_message"
                    type="text"
                    class="input-main w-full"
                    :placeholder="$t('enterVariables')"
                  />
                  
                  <div class="mt-2 flex flex-wrap gap-2" v-if="currentFormFields.length > 0">
          <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('availableParameters') }}:</div>
          <button 
                      v-for="field in currentFormFields" 
            :key="field.id"
                      @click="insertParameter(field.placeholder, 'currentRow.config.admin_message')"
            class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
          >
            {{ field.label }} ({{ field.placeholder }})
          </button>
        </div>
      </div>
    </div>
    
            <!-- Admin Payment Success Notification - Only for Gravity Forms -->
              <div v-if="currentRow.form_type === 'gravity_forms'" class="mb-6 p-4 border border-gray-200 rounded-lg">
      <div class="flex items-start gap-3">
        <label class="switch peer">
          <input
                      v-model="currentRow.config.admin_send_on_payment_success"
            type="checkbox"
          />
          <div class="slider round"></div>
        </label>
        <div class="flex flex-col">
                  <div class="text font-normal text-[#4D4D4D]">
            {{ $t('sendSmsOnPaymentSuccess') }}
          </div>
                  <div class="sub-text text-xs font-normal text-[#696767]">
            {{ $t('sendSmsOnPaymentSuccessDescription') }}
          </div>
        </div>
      </div>
      
                <div v-if="currentRow.config.admin_send_on_payment_success" class="pl-12 mt-3">
        <div class="mb-3">
                  <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
          <input
                      v-model="currentRow.config.admin_payment_success_template_id"
            type="number"
            class="input-main w-full"
            :placeholder="$t('templateId')"
          />
        </div>
        
        <div>
                  <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('paymentSuccessMessage') }}</label>
                  <input
                      v-model="currentRow.config.admin_payment_success_message"
                    type="text"
                    class="input-main w-full"
                    :placeholder="$t('enterVariables')"
                  />
          
          <div class="mt-2 flex flex-wrap gap-2">
            <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('paymentParameters') }}:</div>
            <div class="flex flex-wrap gap-2">
              <button 
                v-for="param in paymentParameters" 
                :key="param.id"
                          @click="insertParameter(param.placeholder, 'currentRow.config.admin_payment_success_message')"
                class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
              >
                {{ param.label }} ({{ param.placeholder }})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
            <!-- Admin Payment Failure Notification - Only for Gravity Forms -->
              <div v-if="currentRow.form_type === 'gravity_forms'" class="mb-6 p-4 border border-gray-200 rounded-lg">
      <div class="flex items-start gap-3">
        <label class="switch peer">
          <input
                      v-model="currentRow.config.admin_send_on_payment_failure"
            type="checkbox"
          />
          <div class="slider round"></div>
        </label>
        <div class="flex flex-col">
                  <div class="text font-normal text-[#4D4D4D]">
            {{ $t('sendSmsOnPaymentFailure') }}
          </div>
                  <div class="sub-text text-xs font-normal text-[#696767]">
            {{ $t('sendSmsOnPaymentFailureDescription') }}
          </div>
        </div>
      </div>
      
                <div v-if="currentRow.config.admin_send_on_payment_failure" class="pl-12 mt-3">
        <div class="mb-3">
                  <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
          <input
                      v-model="currentRow.config.admin_payment_failure_template_id"
            type="number"
            class="input-main w-full"
            :placeholder="$t('templateId')"
          />
        </div>
        
        <div>
                  <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('paymentFailureMessage') }}</label>
                  <input
                      v-model="currentRow.config.admin_payment_failure_message"
                    type="text"
                    class="input-main w-full"
                    :placeholder="$t('enterVariables')"
                  />
          
          <div class="mt-2 flex flex-wrap gap-2">
            <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('paymentParameters') }}:</div>
            <div class="flex flex-wrap gap-2">
              <button 
                v-for="param in paymentParameters" 
                :key="param.id"
                          @click="insertParameter(param.placeholder, 'currentRow.config.admin_payment_failure_message')"
                class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
              >
                {{ param.label }} ({{ param.placeholder }})
              </button>
                      </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
            <!-- User Configuration -->
            <div v-if="editRecipientTypes.includes('user')" class="mb-6 p-4 border border-gray-200 rounded-lg">
              <h3 class="text-lg font-medium mb-3">{{ $t('userConfiguration') }}</h3>
              
              <div class="mb-4">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('userPhoneField') }}</label>
                <select
                  v-model="currentRow.config.user_phone_field"
                  class="input-main w-full"
                >
                  <option value="">{{ $t('selectPhoneField') }}</option>
                  <option v-for="field in currentFormFields" :key="field.id" :value="field.id">
                    {{ field.label || field.id }}
                  </option>
                </select>
                <div v-if="currentFormFields.length === 0 && currentRow.form_id" class="text-amber-600 text-sm mt-1">
                  {{ $t('noFieldsFound') || 'No fields found.' }}
                </div>
              </div>
              
              <div class="mb-4">
                  <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
      <input
                  v-model="currentRow.config.user_template_id"
        type="number"
        class="input-main w-full"
        :placeholder="$t('templateId')"
      />
    </div>
    
              <div class="mb-4">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('messageTemplate') }}</label>
                <div class="relative">
                  <input
                    v-model="currentRow.config.user_message"
                    type="text"
                    class="input-main w-full"
                    :placeholder="$t('enterVariables')"
                  />
                  
                  <div class="mt-2 flex flex-wrap gap-2" v-if="currentFormFields.length > 0">
                    <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('availableParameters') }}:</div>
            <button 
                      v-for="field in currentFormFields" 
                      :key="field.id"
                      @click="insertParameter(field.placeholder, 'currentRow.config.user_message')"
              class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
            >
                      {{ field.label }} ({{ field.placeholder }})
            </button>
        </div>
      </div>
    </div>
    
              <!-- User Payment Success Notification - Only for Gravity Forms -->
              <div v-if="currentRow.form_type === 'gravity_forms'" class="mb-6 p-4 border border-gray-200 rounded-lg">
      <div class="flex items-start gap-3">
        <label class="switch peer">
          <input
                      v-model="currentRow.config.user_send_on_payment_success"
            type="checkbox"
          />
          <div class="slider round"></div>
        </label>
        <div class="flex flex-col">
                  <div class="text font-normal text-[#4D4D4D]">
                      {{ $t('sendSmsOnPaymentSuccess') }}
          </div>
                  <div class="sub-text text-xs font-normal text-[#696767]">
                      {{ $t('sendSmsOnPaymentSuccessDescription') }}
          </div>
        </div>
      </div>
      
                <div v-if="currentRow.config.user_send_on_payment_success" class="pl-12 mt-3">
        <div class="mb-3">
                  <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
          <input
                      v-model="currentRow.config.user_payment_success_template_id"
            type="number"
            class="input-main w-full"
            :placeholder="$t('templateId')"
          />
        </div>
        
        <div>
                    <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('paymentSuccessMessage') }}</label>
                  <input
                      v-model="currentRow.config.user_payment_success_message"
                    type="text"
                    class="input-main w-full"
                    :placeholder="$t('enterVariables')"
                  />
          
          <div class="mt-2 flex flex-wrap gap-2">
            <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('paymentParameters') }}:</div>
            <div class="flex flex-wrap gap-2">
              <button 
                v-for="param in paymentParameters" 
                :key="param.id"
                          @click="insertParameter(param.placeholder, 'currentRow.config.user_payment_success_message')"
                class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
              >
                {{ param.label }} ({{ param.placeholder }})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
              <!-- User Payment Failure Notification - Only for Gravity Forms -->
              <div v-if="currentRow.form_type === 'gravity_forms'" class="mb-6 p-4 border border-gray-200 rounded-lg">
      <div class="flex items-start gap-3">
        <label class="switch peer">
          <input
                      v-model="currentRow.config.user_send_on_payment_failure"
            type="checkbox"
          />
          <div class="slider round"></div>
        </label>
        <div class="flex flex-col">
                    <div class="text font-normal text-[#4D4D4D]">
                      {{ $t('sendSmsOnPaymentFailure') }}
          </div>
                    <div class="sub-text text-xs font-normal text-[#696767]">
                      {{ $t('sendSmsOnPaymentFailureDescription') }}
          </div>
        </div>
      </div>
      
                <div v-if="currentRow.config.user_send_on_payment_failure" class="pl-12 mt-3">
        <div class="mb-3">
                    <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
          <input
                      v-model="currentRow.config.user_payment_failure_template_id"
            type="number"
            class="input-main w-full"
            :placeholder="$t('templateId')"
          />
        </div>
        
        <div>
                    <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('paymentFailureMessage') }}</label>
                    <input
                      v-model="currentRow.config.user_payment_failure_message"
                      type="text"
                      class="input-main w-full"
                      :placeholder="$t('enterVariables')"
                    />
          
          <div class="mt-2 flex flex-wrap gap-2">
            <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('paymentParameters') }}:</div>
            <div class="flex flex-wrap gap-2">
              <button 
                v-for="param in paymentParameters" 
                :key="param.id"
                          @click="insertParameter(param.placeholder, 'currentRow.config.user_payment_failure_message')"
                class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
              >
                {{ param.label }} ({{ param.placeholder }})
              </button>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
        <div class="modal-footer">
          <button @click="closeModal" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
        {{ $t('cancel') }}
      </button>
      <button 
        @click="saveRowConfig" 
            class="BTNMAIN bg-[#12BA6C] flex gap-2 items-center"
            :disabled="editRecipientTypes.length === 0 || modalLoading"
            :class="{ 'opacity-50 cursor-not-allowed': editRecipientTypes.length === 0 || modalLoading }"
      >
            <i class="icon-smsir-save text-xl text-white"></i>
            <span class="text-white text-base">
              {{ isNewRow ? $t('addForm') : $t('SaveChanges') }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirmation" class="modal">
      <div class="modal-content max-w-[400px]">
        <div class="modal-header">
          <h2 class="text-[18px] text-[#0D0D0D]">{{ $t('confirmDelete') }}</h2>
          <button @click="showDeleteConfirmation = false" class="text-2xl">&times;</button>
        </div>
        
        <div class="modal-body">
          <p class="mb-4 text-[#4D4D4D]">{{ $t('form.delete_confirm_message') }}</p>
        </div>
        
        <div class="modal-footer">
     
          <button @click="showDeleteConfirmation = false" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
            {{ $t('cancel') }}
          </button>
          <button @click="confirmDeleteRow" class="px-4 py-2 bg-red-500 text-white rounded-full flex items-center gap-2 text-base">
                <i class="icon-smsir-trash-bin-trash text-xl text-white"></i>
                {{ $t('common.delete') }}
      </button>
    </div>
  </div>
</div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import LoadingSpinner from '../LoadingSpinner.vue';

export default {
  name: "FormSettings",
  components: {
    LoadingSpinner
  },
  data() {
    return {
      loading: true,
      modalLoading: false,
      notificationRows: [],
      availableForms: [],
      currentFormFields: [],
      showEditModal: false,
      showDeleteConfirmation: false,
      currentRowIndex: null,
      isNewRow: false,
      currentRow: {
        form_type: '',
        form_id: '',
        recipient_types: [],
        config: {
      admin_mobile_numbers: '',
      user_phone_field: '',
      admin_template_id: '', 
      admin_message: '',
        user_template_id: '',
        user_message: '',
      admin_send_on_payment_success: false,
      admin_payment_success_template_id: '',
      admin_payment_success_message: '',
        user_send_on_payment_success: false,
        user_payment_success_template_id: '',
        user_payment_success_message: '',
      admin_send_on_payment_failure: false,
      admin_payment_failure_template_id: '',
        admin_payment_failure_message: '',
        user_send_on_payment_failure: false,
        user_payment_failure_template_id: '',
        user_payment_failure_message: ''
        }
      },
      editRecipientTypes: [],
      currentFormId: null,
      formTypes: {
        gravity_forms: 'Gravity Forms',
        contact_form_7: 'Contact Form 7',
        wpforms: 'WPForms'
      },
      availableFormTypes: [],
      paymentParameters: [
     {
    id: "id",
    label: this.$t('paymentParams.id'),
    placeholder: "#id#",
     },
     {
       id: "formid",
       label: this.$t('paymentParams.formid'),
       placeholder: "#formid#",
     },
     {
       id: "amount",
       label: this.$t('paymentParams.amount'),
       placeholder: "#amount#",
     },
     {
       id: "transaction",
       label: this.$t('paymentParams.transaction'),
       placeholder: "#transaction#",
     },
     {
       id: "gateway",
       label: this.$t('paymentParams.gateway'),
       placeholder: "#gateway#",
     },
        ],
      saving: false,
      loadingForms: false,
      initialized: false,
      gravityFormsInactive: false,
      rowToDelete: null,
    };
  },
  created() {
  },
  mounted() {
    this.initialize();
  },
  watch: {
    active(newValue) {
      if (newValue && !this.initialized) {
        this.initialize();
      }
    },
    editRecipientTypes(newValue) {
      if (newValue.includes('user') && this.currentRow.form_id && this.currentFormFields.length === 0) {
        this.loadFormFields(this.currentRow);
      }
    },
    'currentRow.form_id': function(newValue) {
      if (newValue && this.currentRow.form_type) {
        this.loadFormFields(this.currentRow);
      }
    }
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_get_form_notification_rows",
            nonce: window.sms_ir_login.nonce
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            }
          }
        );

        if (response.data.success) {
          const rows = response.data.data;
          this.notificationRows = Array.isArray(rows) ? rows : [];
          this.gravityFormsInactive = false;
        } else {
          if (response.data?.data === 'gravity_forms_inactive') {
            this.gravityFormsInactive = true;
          } else {
            console.error("Failed to load settings:", response.data.data);
            this.showLoadErrorToast();
          }
        }
      } catch (error) {
        console.error("Error loading settings:", error.response?.data);
        if (error.response?.data?.data === 'gravity_forms_inactive' || error.response?.data === 'gravity_forms_inactive') {
          this.gravityFormsInactive = true;
        } else {
          this.showLoadErrorToast();
        }
      } finally {
        this.loading = false;
      }
    },
    recipientTypeChanged(index) {
      const row = this.notificationRows[index];

      if (!row.recipient_type) {
        return;
      }

      if (!row.form_id) {
        row.recipient_type = "";

        Swal.fire({
          text: this.$t("pleaseSelectForm"),
          icon: "warning",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
        return;
      }

      this.configureRow(index);
    },
    async initialize() {
      this.initialized = true;
      this.loading = true;
      try {
        // Check which form plugins are active
        await this.checkActivePlugins();
        // Load settings
        await this.loadSettings();
      } catch (e) {
        console.error("Error during initialization:", e);
      } finally {
        this.loading = false;
      }
    },
    async checkActivePlugins() {
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_get_forms",
            nonce: window.sms_ir_login.nonce,
            form_type: 'check_plugins'
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            }
          }
        );

        // Reset available form types
        this.availableFormTypes = ['gravity_forms'];

        // Add Contact Form 7 if it's active
        if (response.data.success && !response.data.data.includes('contact_form_7_inactive')) {
          this.availableFormTypes.push('contact_form_7');
        }

        // Add WPForms if it's active
        if (response.data.success && !response.data.data.includes('wpforms_inactive')) {
          this.availableFormTypes.push('wpforms');
        }

        // If current form type is not available, reset to default
        if (!this.availableFormTypes.includes(this.currentRow.form_type)) {
          this.currentRow.form_type = 'gravity_forms';
          this.currentRow.form_id = '';
          this.currentFormFields = [];
        }
      } catch (error) {
        console.error("Error checking plugins:", error);
        // Default to just Gravity Forms if there's an error
        this.availableFormTypes = ['gravity_forms'];
      }
    },
    async loadAvailableForms() {
      if (!this.currentRow.form_type) {
        return; // Don't load if no form type is selected
      }

      // Reset forms and inactive state
      this.availableForms = [];
      this.gravityFormsInactive = false;
      this.loadingForms = true;

      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_get_forms",
            nonce: window.sms_ir_login.nonce,
            form_type: this.currentRow.form_type
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            }
          }
        );

        if (response.data.success) {
          this.availableForms = response.data.data || [];
          this.gravityFormsInactive = false;
        } else {
          if (response.data?.data === 'gravity_forms_inactive' || 
              response.data?.data === 'contact_form_7_inactive' || 
              response.data?.data === 'wpforms_inactive') {
            this.gravityFormsInactive = true;
            this.availableForms = []; // Clear forms when plugin is inactive
          } else if (response.data?.data === 'no_forms_found') {
            this.availableForms = []; // Clear forms when no forms found
            Swal.fire({
              text: this.$t("noFormsFound"),
              icon: "warning",
              toast: true,
              position: "top-start",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              customClass: {
                popup: "smsir-swal"
              }
            });
          } else {
            throw new Error(response.data.data || this.$t("failedToLoadForms"));
          }
        }
      } catch (error) {
        console.error("Error loading forms:", error);
        if (error.response?.data?.data === 'gravity_forms_inactive' || 
            error.response?.data?.data === 'contact_form_7_inactive' || 
            error.response?.data?.data === 'wpforms_inactive') {
          this.gravityFormsInactive = true;
          this.availableForms = []; // Clear forms when plugin is inactive
        } else {
        Swal.fire({
            text: this.$t("failedToLoadForms") + " - " + (error.message || "Unknown error"),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
              popup: "smsir-swal"
            }
        });
        }
      } finally {
        this.loadingForms = false;
      }
    },
    openEditModal() {
      this.isNewRow = true;
      this.showEditModal = true;
      this.currentRow = {
        form_type: '',
        form_id: '',
        recipient_types: [],
        config: {
          admin_mobile_numbers: '',
          user_phone_field: '',
          admin_template_id: '',
          admin_message: '',
          user_template_id: '',
          user_message: '',
          admin_send_on_payment_success: false,
          admin_payment_success_template_id: '',
          admin_payment_success_message: '',
          user_send_on_payment_success: false,
          user_payment_success_template_id: '',
          user_payment_success_message: '',
          admin_send_on_payment_failure: false,
          admin_payment_failure_template_id: '',
          admin_payment_failure_message: '',
          user_send_on_payment_failure: false,
          user_payment_failure_template_id: '',
          user_payment_failure_message: ''
        }
      };
      this.editRecipientTypes = [];
      this.currentFormFields = [];
    },
    editRow(index) {
      this.isNewRow = false;
      this.currentRowIndex = index;
      this.showEditModal = true;
      this.configureRow(index);
    },
    deleteRow(index) {
      this.rowToDelete = index;
      this.showDeleteConfirmation = true;
    },
    async confirmDeleteRow() {
      if (this.rowToDelete !== null) {
        this.notificationRows.splice(this.rowToDelete, 1);
        this.rowToDelete = null;
        this.showDeleteConfirmation = false;
        
        // Save settings automatically
        await this.saveAllSettings();
      }
    },
    async saveAllSettings() {
      this.saving = true;

      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_save_form_notification_rows",
            nonce: window.sms_ir_login.nonce,
            rows: JSON.stringify(this.notificationRows),
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.success) {
          Swal.fire({
            text: this.$t("SettingsSavedSuccessfully"),
            icon: "success",
            toast: true,
            position: "top-start",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: "smsir-swal",
            },
          });
        } else {
          throw new Error(response.data.data || this.$t("SettingsSaveError"));
        }
      } catch (error) {
        console.error("Error saving settings:", error);
        Swal.fire({
          text: this.$t("failedToSaveSettings"),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
      } finally {
        this.saving = false;
      }
    },
    async configureRow(index) {
      this.currentRowIndex = index;
      const row = this.notificationRows[index];

      if (!row.form_id) {
        Swal.fire({
          text: this.$t("pleaseSelectForm"),
          icon: "warning",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
        return;
      }

      this.modalLoading = true;

      try {
        // Load available forms for the current form type
        await this.loadAvailableFormsForEdit(row.form_type);

        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_get_form_fields",
            nonce: window.sms_ir_login.nonce,
            form_id: row.form_id,
            form_type: row.form_type
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            },
          }
        );

        if (response.data.success) {
          this.currentFormFields = response.data.data || [];

          // Format placeholders based on form type
          this.currentFormFields.forEach((field) => {
            if (row.form_type === 'contact_form_7') {
              field.placeholder = `#${field.id}#`; // CF7 format: #[field-name]#
            } else {
              field.placeholder = `#${field.type.replace("_", "")}${field.id}#`; // Other forms format
            }
          });
        }

        // Initialize currentRow with existing data
        this.currentRow = {
          form_type: row.form_type,
          form_id: row.form_id,
          recipient_types: row.recipient_types || [],
          config: {
  admin_mobile_numbers: row.config?.admin_mobile_numbers || '',
  user_phone_field: row.config?.user_phone_field || '',
          admin_template_id: row.config?.admin_template_id || '',
  admin_message: row.config?.admin_message || '',
          user_template_id: row.config?.user_template_id || '',
          user_message: row.config?.user_message || '',
  admin_send_on_payment_success: row.config?.admin_send_on_payment_success || false,
  admin_payment_success_template_id: row.config?.admin_payment_success_template_id || '',
  admin_payment_success_message: row.config?.admin_payment_success_message || '',
          user_send_on_payment_success: row.config?.user_send_on_payment_success || false,
          user_payment_success_template_id: row.config?.user_payment_success_template_id || '',
          user_payment_success_message: row.config?.user_payment_success_message || '',
  admin_send_on_payment_failure: row.config?.admin_send_on_payment_failure || false,
  admin_payment_failure_template_id: row.config?.admin_payment_failure_template_id || '',
          admin_payment_failure_message: row.config?.admin_payment_failure_message || '',
          user_send_on_payment_failure: row.config?.user_send_on_payment_failure || false,
          user_payment_failure_template_id: row.config?.user_payment_failure_template_id || '',
          user_payment_failure_message: row.config?.user_payment_failure_message || ''
          }
        };

        // Set recipient types
        this.editRecipientTypes = [...this.currentRow.recipient_types];

        // Set current form ID for display
        this.currentFormId = row.form_id;
      } catch (error) {
        console.error("Error configuring row:", error);
        Swal.fire({
          text: this.$t("failedToLoadFormFields"),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
      } finally {
        this.modalLoading = false;
      }
    },
    async loadAvailableFormsForEdit(formType) {
      this.loadingForms = true;
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_get_forms",
            nonce: window.sms_ir_login.nonce,
            form_type: formType
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            }
          }
        );

        if (response.data.success) {
          this.availableForms = response.data.data || [];
        }
      } catch (error) {
        console.error("Error loading forms for edit:", error);
        this.availableForms = [];
      } finally {
        this.loadingForms = false;
      }
    },
    closeModal() {
      this.showEditModal = false;
  this.currentRowIndex = null;
  this.currentFormFields = [];
      this.currentRow = {
        form_type: '',
        form_id: '',
        recipient_types: [],
        config: {
    admin_mobile_numbers: '',
    user_phone_field: '',
    admin_template_id: '',
    admin_message: '',
        user_template_id: '',
        user_message: '',
    admin_send_on_payment_success: false,
    admin_payment_success_template_id: '',
    admin_payment_success_message: '',
        user_send_on_payment_success: false,
        user_payment_success_template_id: '',
        user_payment_success_message: '',
    admin_send_on_payment_failure: false,
    admin_payment_failure_template_id: '',
        admin_payment_failure_message: '',
        user_send_on_payment_failure: false,
        user_payment_failure_template_id: '',
        user_payment_failure_message: ''
        }
      };
      this.editRecipientTypes = [];
      this.currentFormId = null;
      this.isNewRow = false;
    },
    async saveRowConfig() {
      if (this.editRecipientTypes.length === 0) return;
      
      // Validate form selection
      if (!this.currentRow.form_id) {
        Swal.fire({
          text: this.$t('pleaseSelectForm'),
          icon: 'warning',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'smsir-swal'
          }
        });
        return;
      }
      
      // Get the selected form's title
      const selectedForm = this.availableForms.find(form => form.id === this.currentRow.form_id);
      const formTitle = selectedForm ? selectedForm.title : '';
      
      // Create the row object
      const row = {
        form_type: this.currentRow.form_type,
        form_id: this.currentRow.form_id,
        form_title: formTitle, // Save the form title
        recipient_type: this.editRecipientTypes.length === 1 ? this.editRecipientTypes[0] : 'both',
        recipient_types: [...this.editRecipientTypes],
        config: {
          admin_mobile_numbers: this.currentRow.config.admin_mobile_numbers,
          user_phone_field: this.currentRow.config.user_phone_field,
          admin_template_id: this.currentRow.config.admin_template_id,
          admin_message: this.currentRow.config.admin_message,
          user_template_id: this.currentRow.config.user_template_id,
          user_message: this.currentRow.config.user_message,
          admin_send_on_payment_success: this.currentRow.config.admin_send_on_payment_success,
          admin_payment_success_template_id: this.currentRow.config.admin_payment_success_template_id,
          admin_payment_success_message: this.currentRow.config.admin_payment_success_message,
          user_send_on_payment_success: this.currentRow.config.user_send_on_payment_success,
          user_payment_success_template_id: this.currentRow.config.user_payment_success_template_id,
          user_payment_success_message: this.currentRow.config.user_payment_success_message,
          admin_send_on_payment_failure: this.currentRow.config.admin_send_on_payment_failure,
          admin_payment_failure_template_id: this.currentRow.config.admin_payment_failure_template_id,
          admin_payment_failure_message: this.currentRow.config.admin_payment_failure_message,
          user_send_on_payment_failure: this.currentRow.config.user_send_on_payment_failure,
          user_payment_failure_template_id: this.currentRow.config.user_payment_failure_template_id,
          user_payment_failure_message: this.currentRow.config.user_payment_failure_message
        }
      };
      
      if (this.isNewRow) {
        // Add new row
        this.notificationRows.push(row);
      } else {
        // Update existing row
        this.notificationRows[this.currentRowIndex] = row;
      }
      
      // Close the modal
      this.closeModal();
      
      // Save settings automatically
      await this.saveAllSettings();
      
      // Show success message
      Swal.fire({
        text: this.$t(this.isNewRow ? 'rowAddedSuccessfully' : 'rowConfigSaved'),
        icon: 'success',
        toast: true,
        position: 'top-start',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        customClass: {
          popup: 'smsir-swal'
        }
      });
    },
    async loadFormFields(row) {
      if (!row || !row.form_id || !row.form_type) return;
      
      this.modalLoading = true;
      this.currentFormFields = []; // Clear current fields before loading new ones
      
      console.log('Loading form fields for:', row.form_type, row.form_id);
      
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_get_form_fields",
            nonce: window.sms_ir_login.nonce,
            form_id: row.form_id,
            form_type: row.form_type
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.success) {
          const fields = response.data.data || [];
          console.log('Received fields:', fields);

          // Format placeholders based on form type
          fields.forEach((field) => {
            if (row.form_type === 'contact_form_7') {
              field.placeholder = `#${field.id}#`; // CF7 format: #[field-name]#
            } else {
              field.placeholder = `#${field.type?.replace("_", "") || ""}${field.id}#`; // Other forms format
            }
          });

          this.currentFormFields = fields;
          console.log('Updated currentFormFields:', this.currentFormFields);
          
          if (this.currentFormFields.length === 0) {
            Swal.fire({
              text: this.$t("noFormFieldsFound") || "No form fields found for this form",
              icon: "warning",
              toast: true,
              position: "top-start",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              customClass: {
                popup: "smsir-swal",
              },
            });
          }
          
          return fields;
        } else {
          throw new Error(response.data.data || "Failed to load form fields");
        }
      } catch (error) {
        console.error("Error loading form fields:", error);
        Swal.fire({
          text: this.$t("failedToLoadFormFields") + (error.message ? ": " + error.message : ""),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
        return [];
      } finally {
        this.modalLoading = false;
      }
    },
    showLoadErrorToast() {
          Swal.fire({
        text: this.$t("failedToLoadSettings"),
          icon: "error",
            toast: true,
            position: "top-start",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
          popup: "smsir-swal"
        }
        });
      },
    getFormTypeText(formType) {
      return this.formTypes[formType] || formType;
    },
    async handleFormTypeChange() {
      // Reset form ID and fields
      this.currentRow.form_id = '';
      this.currentFormFields = [];
      
      // Reset recipient types when changing form type
      this.editRecipientTypes = [];
      
      if (this.currentRow.form_type) {
        this.loadingForms = true;
        try {
          await this.loadAvailableForms();
          
          // If no forms are available after loading, show warning
          if (this.availableForms.length === 0) {
            Swal.fire({
              text: this.$t("noFormsFound"),
              icon: "warning",
              toast: true,
              position: "top-start",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              customClass: {
                popup: "smsir-swal"
              }
            });
          }
        } catch (error) {
          console.error("Error loading forms:", error);
          Swal.fire({
            text: this.$t("failedToLoadForms"),
            icon: "error",
            toast: true,
            position: "top-start",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: "smsir-swal"
            }
          });
        } finally {
          this.loadingForms = false;
        }
      }
    },
    getFormTitle(formId, formType) {
      // First try to find the form in the notification rows (where we store the title)
      const row = this.notificationRows.find(r => r.form_id === formId);
      if (row && row.form_title) {
        return row.form_title;
      }
      
      // If not found in notification rows, try to find it in available forms
      const form = this.availableForms.find(f => f.id === formId);
      if (form) {
        return form.title;
      }
      
      // If still not found, return the form ID
      return formId;
    },
    getRecipientText(recipientType) {
      // Handle array format (new format)
      if (Array.isArray(recipientType)) {
        if (recipientType.includes('admin') && recipientType.includes('user')) {
          return this.$t('admin') + ' & ' + this.$t('user');
        } else if (recipientType.includes('admin')) {
          return this.$t('admin');
        } else if (recipientType.includes('user')) {
          return this.$t('user');
        }
        return this.$t('selectRecipient');
      }
      
      // Handle string format (old format)
      if (recipientType === 'admin') {
        return this.$t('admin');
      } else if (recipientType === 'user') {
        return this.$t('user');
      } else if (recipientType === 'both') {
        return this.$t('admin') + ' & ' + this.$t('user');
      }
      return this.$t('selectRecipient');
    },
    insertParameter(placeholder, targetField) {
      // Split the target field path (e.g., 'currentRow.config.admin_message')
      const path = targetField.split('.');
      
      // Get the current value of the target field
      let currentValue = this;
      for (const key of path) {
        currentValue = currentValue[key];
      }
      
      // Add the parameter at the end of the current value
      const newValue = currentValue + placeholder;
      
      // Update the value using the path
      let target = this;
      for (let i = 0; i < path.length - 1; i++) {
        target = target[path[i]];
      }
      target[path[path.length - 1]] = newValue;
    },
  },
};
</script>

<style scoped>
.section-subtitle {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 30px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #0D0D0D;
}

.modal-body {
  overflow-y: auto;
  flex: 1;
  padding: 0 16px;
}

.modal-footer {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 16px;
}

.configuration-badge {
  display: inline-block;
  padding: 8.5px 12px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 400;
  background-color: #E5FFF0;
  border: 1px solid #0E8B51;
  color: #0E8B51;
}

.configuration-badge.not-configured {
  background-color: #FFF9E5;
  border: 1px solid #B38600;
  color: #B38600;
}

@media (min-width: 768px) {
  .modal-content {
    width: 600px;
  }
  
  .modal-content.max-w-\[900px\] {
    width: 900px;
  }
  
  .modal-content.max-w-md {
    width: 500px;
  }
}

select {
  background-position: left 0.5rem center;
}
</style>
